/**
 * 대시보드 배치 서버 동기화 — **실행부** (2026-09-16 · 대시보드 v2 V10 · web-plan §2.2).
 *
 * ★★★ **store 를 import 하지 않는다.** 필요한 것을 «포트»로 받는다 —
 *     `dashboardStore` → 이 파일 한 방향이라 순환 import 가 생기지 않는다
 *     (store ↔ registry 순환을 피하려고 위젯 key 를 string 으로 둔 것과 같은 이유).
 *
 * ★★ **깃발이 꺼져 있으면 아무 일도 하지 않는다.** 두 함수 모두 첫 줄에서 돌아간다 —
 *    켜기 전까지 화면 · 저장 · 네트워크가 지금과 한 글자도 다르지 않다.
 *
 * ★ 판정은 `dashboard-layout-sync.ts`(순수)가 하고, 여기서는 **부르고 · 반영하고 · 표식만 쓴다.**
 */
import { DASHBOARD_SERVER_LAYOUT_ENABLED } from '../lib/layout-shell';
import {
  getDashboardLayout, putDashboardLayout, type DashboardLayoutDoc,
} from '../api/dashboard-layout';
import {
  canSync, decideHydrate, readMarker, writeMarker, backupLocalBody, toServerDoc,
  type LocalSnapshot,
} from './dashboard-layout-sync';

/** 모아 올리기 간격 — 끌어 옮기는 동안 매번 올리지 않는다(web-plan §2.2). */
export const PUSH_DEBOUNCE_MS = 800;

export interface SyncPorts {
  /** 지금 store 상태(회사 판 포함 — 거르는 일은 `toServerDoc` 이 한다). */
  getSnapshot: () => LocalSnapshot;
  /** 이 사용자의 로컬 저장본이 있었는가. */
  hasLocal: () => boolean;
  /** 서버 문서로 화면을 교체한다. */
  applyServerDoc: (doc: DashboardLayoutDoc) => void;
  /** 「이 브라우저 배치로 바꾸기」 안내 — **한 번만**(Q-2). 없으면 안 알린다. */
  notifyReplaced?: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** 올리는 중인 사용자 — 겹쳐 올리지 않는다(마지막 것이 다시 예약된다). */
const inFlight = new Set<string>();

/** 검사에서 타이머 · 진행 상태를 초기화한다. */
export function __resetSyncForTest(): void {
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  inFlight.clear();
}

/**
 * hydrate 직후 서버와 맞춘다 — **로컬로 화면을 먼저 그린 뒤**에 부른다.
 *
 * ★★★ 이 함수는 기다리지 않는다(fire-and-forget). `hydrate` 는 **동기 계약**이라
 *     부른 직후 상태를 읽는 곳이 여럿이다(`DashboardBoard.tsx` · 검사 여러 벌).
 *     서버 응답이 화면의 첫 그림을 늦추면 안 된다.
 */
export async function syncOnHydrate(
  userId: string,
  ports: SyncPorts,
  enabled: boolean = DASHBOARD_SERVER_LAYOUT_ENABLED,
): Promise<void> {
  if (!enabled || !canSync(userId)) return;

  const fetched = await getDashboardLayout();
  const decision = decideHydrate({
    fetched, hasLocal: ports.hasLocal(), marker: readMarker(userId),
  });

  switch (decision.do) {
    case 'local-only':
    case 'first-visit':
    case 'same':
      return;

    case 'push-local': {
      const res = await putDashboardLayout(toServerDoc(ports.getSnapshot()), 0, decision.importedFrom);
      if (res.kind === 'ok') writeMarker(userId, { revision: res.revision, at: Date.now() });
      // 409 — 그새 다른 기기가 올렸다. 서버 것이 이기고 로컬 본문은 보관한다(아래와 같은 규칙).
      else if (res.kind === 'conflict' && res.layout) {
        backupLocalBody(userId);
        ports.applyServerDoc(res.layout);
        writeMarker(userId, { revision: res.revision, at: Date.now() });
        ports.notifyReplaced?.();
      }
      return;
    }

    case 'take-server-keep-local-copy':
      /* 이 브라우저는 맞춰 본 적이 없는데 양쪽에 배치가 있다 — 서버가 이기되 **로컬은 버리지 않는다**. */
      backupLocalBody(userId);
      ports.notifyReplaced?.();
      applyFetched(fetched, ports, userId, decision.revision);
      return;

    case 'take-server':
      applyFetched(fetched, ports, userId, decision.revision);
      return;
  }
}

function applyFetched(
  fetched: Awaited<ReturnType<typeof getDashboardLayout>>,
  ports: SyncPorts, userId: string, revision: number,
): void {
  if (fetched.kind !== 'ok' || !fetched.layout) return;
  ports.applyServerDoc(fetched.layout);
  writeMarker(userId, { revision, at: Date.now() });
}

/**
 * 변경을 모아 서버에 올린다 — 모든 store 변경 꼬리에서 불린다.
 *
 * ★ 로컬 저장은 **이미 끝난 뒤**다(지금과 같음). 여기서 실패해도 배치는 남아 있다 —
 *   표식에 `dirty` 를 남겨 다음 hydrate 때 다시 맞춘다.
 */
export function schedulePush(
  userId: string,
  ports: SyncPorts,
  enabled: boolean = DASHBOARD_SERVER_LAYOUT_ENABLED,
): void {
  if (!enabled || !canSync(userId)) return;

  const existing = timers.get(userId);
  if (existing) clearTimeout(existing);
  timers.set(userId, setTimeout(() => {
    timers.delete(userId);
    void pushNow(userId, ports);
  }, PUSH_DEBOUNCE_MS));
}

async function pushNow(userId: string, ports: SyncPorts): Promise<void> {
  if (inFlight.has(userId)) {
    /* 올리는 중이면 이 변경은 다음 차례로 미룬다 — 겹쳐 올리면 baseRevision 이 엇갈린다. */
    schedulePush(userId, ports, true);
    return;
  }
  inFlight.add(userId);
  try {
    const marker = readMarker(userId);
    const base = marker?.revision ?? 0;
    const res = await putDashboardLayout(toServerDoc(ports.getSnapshot()), base);

    if (res.kind === 'ok') {
      writeMarker(userId, { revision: res.revision, at: Date.now() });
      return;
    }
    if (res.kind === 'conflict') {
      /* 다른 기기가 먼저 올렸다 — 서버 것으로 갈아끼우고 알린다(방금 변경은 로컬 거울에 남아 있다). */
      if (res.layout) {
        backupLocalBody(userId);
        ports.applyServerDoc(res.layout);
        ports.notifyReplaced?.();
      }
      writeMarker(userId, { revision: res.revision, at: Date.now() });
      return;
    }
    /* 못 올렸다(오프라인 · 서버 없음) — 표식에 남겨 다음에 다시 맞춘다. */
    writeMarker(userId, { revision: base, at: Date.now(), dirty: true });
  } finally {
    inFlight.delete(userId);
  }
}
